/**
 * Canvas Slice - 画布管理
 * 
 * 画布服务用于管理 AI 任务的可视化画布，支持节点的创建、连接和流程编排。
 * 
 * API Endpoints:
 * - POST /api/pipeline/canvas - 创建新画布
 * - GET /api/pipeline/canvas - 列出画布
 * - GET /api/pipeline/canvas/:id - 获取画布详情
 * - POST /api/pipeline/canvas/:id/update - 更新画布信息
 * - POST /api/pipeline/canvas/:id/save - 保存画布（完整更新）
 * - GET /api/pipeline/canvas/:id/delete - 删除画布
 * 
 * Canvas Batch API:
 * - POST /api/pipeline/canvas/:id/batch - 批量处理节点与连接的 create/update/delete
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiRequestError, get, post } from '@/lib/api';
import { storage } from '@/lib/auth';
import type { CanvasSettings } from '@/lib/canvasSettings';

// ==================== Types 类型定义 ====================

/** 画布节点 */
export interface CanvasNode {
  id: string;                    // 节点唯一标识符
  canvas_id?: string;            // 画布ID
  org_id?: string;               // 组织ID
  type: string;                  // 节点类型 (start, llm, agent, etc.)
  title?: string;                // 节点标题
  position_x: number;            // X 坐标
  position_y: number;            // Y 坐标
  measured_width: number;        // 节点宽度
  measured_height: number;       // 节点高度
  data: Record<string, unknown>; // 节点数据
  parent_id?: string;            // 父节点ID
  group_id?: string | null;      // 分组ID
  group_position_x?: number | null; // 分组内相对X
  group_position_y?: number | null; // 分组内相对Y
  created_at?: string;           // 创建时间
  created_by?: string;           // 创建人
  created_by_role?: string;      // 创建人角色
  updated_at?: string;           // 更新时间
}

/** 连接点类型 */
export type HandleType = 'left' | 'right' | 'top' | 'bottom';

/** 连接方向 */
export type ConnectionDirection = 'left-to-right' | 'top-to-bottom' | 'auto';

/** 画布连接 */
export interface CanvasConnection {
  id: string;                    // 连接唯一标识符
  canvas_id?: string;            // 画布ID
  org_id?: string;               // 组织ID
  source: string;                // 源节点ID
  target: string;                // 目标节点ID
  source_handle?: HandleType;    // 源连接点
  target_handle?: HandleType;    // 目标连接点
  label?: string;                // 连接标签
  type?: string;                 // 连接类型
  style?: Record<string, unknown>; // 样式配置
  direction?: ConnectionDirection;  // 连接方向
  created_by?: string;           // 创建人
  created_by_role?: string;      // 创建人角色
  created_at?: string;           // 创建时间
  updated_at?: string;           // 更新时间
}

export interface BatchPasteCanvasNodeInput {
  id: string;
  type: string;
  title?: string;
  position_x: number;
  position_y: number;
  measured_width: number;
  measured_height: number;
  data?: Record<string, unknown>;
  parent_id?: string | null;
  group_id?: string | null;
  group_position_x?: number | null;
  group_position_y?: number | null;
}

export interface BatchPasteCanvasConnectionInput {
  id?: string;
  source: string;
  target: string;
  source_handle?: HandleType | null;
  target_handle?: HandleType | null;
  label?: string;
  type?: string;
  style?: Record<string, unknown>;
  direction?: ConnectionDirection;
}

export interface BatchPasteCanvasParams {
  source_canvas_id?: string;
  nodes: BatchPasteCanvasNodeInput[];
  connections?: BatchPasteCanvasConnectionInput[];
  groups?: BatchPasteCanvasGroupInput[];
  position_offset?: {
    x?: number;
    y?: number;
  };
}

export interface CanvasGroup {
  id: string;
  canvas_id?: string;
  org_id?: string;
  name?: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  collapsed?: boolean;
  style?: Record<string, unknown>;
  created_at?: string;
  created_by?: string;
  updated_at?: string;
}

export interface BatchPasteCanvasGroupInput {
  id: string;
  name?: string;
  position_x?: number;
  position_y?: number;
  width: number;
  height: number;
  collapsed?: boolean;
  style?: Record<string, unknown>;
}

export interface BatchPasteCanvasResult {
  id_mapping?: {
    nodes?: Record<string, string>;
    connections?: Record<string, string>;
    groups?: Record<string, string>;
  };
  nodes: CanvasNode[];
  connections: CanvasConnection[];
  groups?: CanvasGroup[];
}

/** 画布 */
export interface Canvas {
  id: string;                    // 画布唯一标识符
  team_id?: string | null;       // 团队ID
  org_id?: string;               // 组织ID
  name: string;                  // 画布名称
  description?: string;          // 画布描述
  image?: string;                // 画布图片
  cover?: string;                // 画布封面资源
  cover_type?: string;           // 画布封面类型
  cover_image?: string;          // 画布封面
  visibility?: string;           // 可见性
  is_public?: boolean;           // 是否公开
  is_shared_with_org?: boolean; // 是否与组织共享
  owner_id?: string | null;      // 有效所有者
  transfer_source?: string | null; // 转移来源
  settings?: CanvasSettings;     // 画布扩展设置
  nodes: CanvasNode[];           // 节点列表
  connections: CanvasConnection[]; // 连接列表
  created_at: string;            // 创建时间
  created_by?: string;           // 创建人ID
  created_by_role?: string;      // 创建人角色
  updated_at?: string;           // 更新时间
}

/** 画布列表项 */
export interface CanvasListItem {
  id: string;              // 画布唯一标识符
  team_id?: string | null; // 团队ID
  org_id?: string;        // 组织ID
  name: string;           // 画布名称
  description?: string;   // 画布描述
  image?: string;         // 画布图片
  cover?: string;         // 画布封面资源
  cover_type?: string;    // 画布封面类型
  cover_image?: string;   // 画布封面
  visibility?: string;    // 可见性
  is_public?: boolean;    // 是否公开
  owner_id?: string | null; // 有效所有者
  transfer_source?: string | null; // 转移来源
  settings?: CanvasSettings; // 画布扩展设置
  created_at: string;     // 创建时间
  created_by?: string;   // 创建人ID
  created_by_role?: string; // 创建人角色
  updated_at?: string;    // 更新时间
}

/** 画布模板 */
export interface CanvasTemplates {
  id: string;                               // 模板唯一标识符
  name: string;                              // 模板名称
  description?: string;                       // 模板描述
  category?: string;                          // 模板分类
  thumbnail?: string;                         // 缩略图URL
  nodes_count?: number;                      // 节点数量
  is_public?: boolean;                       // 是否公开
  usage_count?: number;                       // 使用次数
  created_by?: string;                       // 创建者ID
  canvas_data?: {                            // 画布数据（模板详情时返回）
    nodes: CanvasNode[];
    connections: CanvasConnection[];
  };
  created_at: string;                         // 创建时间
  updated_at?: string;                        // 更新时间
}

// ==================== Request/Response Params 请求/响应参数 ====================

/** 列出画布请求参数 */
export interface CanvasListParams {
  org_id?: string;        // 组织ID
  team?: boolean;         // 是否请求团队项目
  page?: number;          // 页码，默认1
  page_size?: number;    // 每页数量，默认20
}

/** 创建画布请求参数 */
export interface CreateCanvasParams {
  name?: string;                     // 画布名称
  description?: string;              // 画布描述
  image?: string;                    // 画布图片
  cover?: string;                    // 画布封面资源
  cover_type?: string;               // 画布封面类型
  cover_image?: string;              // 画布封面
  visibility?: string;               // 可见性
  team?: boolean;                    // 是否创建团队项目
}

/** 创建画布返回 */
export interface CreateCanvasResult {
  id: string;
  team_id?: string | null;
  name: string;
  description?: string;
  image?: string;
  cover?: string;
  cover_type?: string;
  cover_image?: string;
  visibility?: string;
  owner_id?: string | null;
  transfer_source?: string | null;
  created_at: string;
  created_by?: string;
  created_by_role?: string;
  updated_at?: string;
}

export interface CanvasTransferResult {
  id: string;
  title?: string;
  name?: string;
  visibility?: string;
  team_id?: string | null;
  owner_id?: string | null;
  transfer_source?: string | null;
  created_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

/** 更新画布请求参数 */
export interface UpdateCanvasParams {
  name?: string;              // 画布名称
  description?: string;      // 画布描述
  is_public?: boolean;        // 是否公开
  is_shared_with_org?: boolean; // 是否与组织共享
  settings?: CanvasSettings;   // 画布扩展设置
  updated_by: string;        // 更新人ID
}

/**
 * 保存画布请求参数（完整更新）
 */
export interface SaveCanvasParams {
  org_id?: string;              // 组织ID（可选，默认 default-org）
  updated_by?: string;          // 更新人ID（可选，默认当前用户）
  nodes?: CanvasNode[];        // 节点列表
  connections?: CanvasConnection[]; // 连接列表
}

/** 节点批量写入参数 */
export interface CanvasNodeInput {
  id?: string;
  type: string;
  title?: string;
  position_x: number;
  position_y: number;
  measured_width: number;
  measured_height: number;
  data?: Record<string, unknown>;
  parent_id?: string | null;
}

/** 连接批量写入参数 */
export interface CanvasConnectionInput {
  id?: string;
  source: string;              // 源节点ID
  target: string;              // 目标节点ID
  source_handle?: HandleType;   // 源连接点
  target_handle?: HandleType;   // 目标连接点
  label?: string;              // 连接标签
  type?: string;               // 连接类型
  style?: Record<string, unknown>; // 样式配置
  direction?: ConnectionDirection; // 连接方向
}

export interface BatchMutation<T> {
  create?: T[];
  update?: T[];
  delete?: string[];
}

export interface BatchCanvasChangesParams {
  nodes?: BatchMutation<CanvasNodeInput>;
  connections?: BatchMutation<CanvasConnectionInput>;
}

export interface BatchCanvasChangesResult {
  nodes: {
    created: CanvasNode[];
    updated: CanvasNode[];
    deleted: string[];
  };
  connections: {
    created: CanvasConnection[];
    updated: CanvasConnection[];
    deleted: string[];
  };
}

export type CanvasCollaborationPermission = 'read' | 'write';

export type CanvasCollaborationRole =
  | 'owner'
  | 'team-editor'
  | 'org-viewer'
  | 'share-readonly';

export type CanvasCollaborationBootstrapStatus =
  | 'pending'
  | 'initializing'
  | 'ready'
  | 'failed';

export interface CanvasCollaborationSnapshotMeta {
  name?: string;
  description?: string;
  settings?: Record<string, unknown>;
}

export interface CanvasCollaborationSnapshot {
  meta: CanvasCollaborationSnapshotMeta;
  nodes: CanvasNode[];
  edges: CanvasConnection[];
  groups: CanvasGroup[];
}

export interface CanvasCollaborationAuthParams {
  canvasId: string;
  share_token?: string;
}

export interface CanvasCollaborationAuthResult {
  canvas_id: string;
  room_id: string;
  role: CanvasCollaborationRole;
  permission: CanvasCollaborationPermission;
  provider: string;
  provider_token: string;
  expires_at: string;
  bootstrap_required: boolean;
  snapshot_version: number;
}

export interface CanvasCollaborationBootstrapParams {
  canvasId: string;
  share_token?: string;
}

export interface CanvasCollaborationBootstrapResult {
  canvas_id: string;
  room_id: string;
  snapshot_version: number;
  bootstrap_status: CanvasCollaborationBootstrapStatus;
  bootstrap_source?: string;
  snapshot: CanvasCollaborationSnapshot;
}

export interface CompleteCanvasCollaborationBootstrapParams {
  canvasId: string;
  room_id: string;
  snapshot_version: number;
  provider_document_id?: string;
}

export interface CompleteCanvasCollaborationBootstrapResult {
  canvas_id: string;
  room_id: string;
  bootstrap_status: Extract<CanvasCollaborationBootstrapStatus, 'ready'>;
  snapshot_version: number;
}

export interface SaveCanvasCollaborationSnapshotParams {
  canvasId: string;
  room_id: string;
  base_snapshot_version: number;
  source: string;
  mutation_source: string;
  snapshot: CanvasCollaborationSnapshot;
}

export interface SaveCanvasCollaborationSnapshotResult {
  canvas_id: string;
  snapshot_version: number;
  saved_at: string;
}

export interface CanvasCollaborationHistoryParams {
  canvasId: string;
  page?: number;
  page_size?: number;
  share_token?: string;
}

export interface CanvasCollaborationHistoryItem {
  snapshot_version: number;
  source: string;
  mutation_source: string;
  created_by?: string;
  created_at: string;
}

export interface CanvasCollaborationHistoryResult {
  canvas_id: string;
  page: number;
  page_size: number;
  items: CanvasCollaborationHistoryItem[];
}

export interface RestoreCanvasCollaborationSnapshotParams {
  canvasId: string;
  snapshot_version: number;
}

export interface RestoreCanvasCollaborationSnapshotResult {
  canvas_id: string;
  snapshot_version: number;
  saved_at: string;
}

/** 从模板创建画布请求参数 */
export interface CreateFromTemplateParams {
  template_id: string;  // 模板ID
  name: string;         // 新画布名称
  description?: string; // 画布描述
  user_id: string;       // 用户ID
  org_id?: string;      // 组织ID
  project_id?: string;  // 项目ID
}

/** 画布列表响应 */
export interface CanvasListResponse {
  items: CanvasListItem[];  // 画布列表
  total: number;            // 总数量
  page: number;              // 当前页码
  page_size: number;         // 每页数量
}

interface FetchCanvasListApiResponse {
  code: number;
  message: string;
  data: {
    data: CanvasListItem[];
    page: number;
    size: number;
    total: number;
  };
}

interface CanvasMutationEnvelope<T> {
  code?: number | string;
  message?: string;
  data?: T;
}

interface CanvasApiEnvelope<T> {
  code: number | string;
  message: string;
  data: T;
}

function isCanvasApiSuccess(code: unknown): boolean {
  return code === undefined || code === 0 || code === 'SUCCESS';
}

const BATCH_CANVAS_CHANGES_MAX_ATTEMPTS = 3;
const BATCH_CANVAS_CHANGES_RETRY_DELAY_MS = 500;

function waitForBatchCanvasRetry(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function shouldRetryBatchCanvasChanges(error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    return error.status === undefined || error.status >= 500;
  }

  if (error instanceof TypeError) {
    return true;
  }

  if (error instanceof Error) {
    return /failed to fetch|network|load failed/i.test(error.message);
  }

  return false;
}

function isCanvasMutationEnvelope<T>(
  value: T | CanvasMutationEnvelope<T>,
): value is CanvasMutationEnvelope<T> {
  return typeof value === 'object'
    && value !== null
    && ('code' in value || 'message' in value || 'data' in value);
}

function upsertCanvasNode(nodes: CanvasNode[], nextNode: CanvasNode): CanvasNode[] {
  const index = nodes.findIndex((node) => node.id === nextNode.id);
  if (index === -1) {
    return [...nodes, nextNode];
  }

  const nextNodes = [...nodes];
  nextNodes[index] = nextNode;
  return nextNodes;
}

function buildShareTokenQuery(shareToken?: string) {
  const query = new URLSearchParams();
  if (shareToken) {
    query.set('share_token', shareToken);
  }

  return query.toString();
}

function upsertCanvasConnection(
  connections: CanvasConnection[],
  nextConnection: CanvasConnection,
): CanvasConnection[] {
  const index = connections.findIndex((connection) => connection.id === nextConnection.id);
  if (index === -1) {
    return [...connections, nextConnection];
  }

  const nextConnections = [...connections];
  nextConnections[index] = nextConnection;
  return nextConnections;
}

/** 模板列表响应 */
export interface TemplatesListResponse {
  templates: CanvasTemplates[];  // 模板列表
  total: number;                   // 总数量
  page: number;                     // 当前页码
  page_size: number;               // 每页数量
}

/** 模板列表请求参数 */
export interface TemplatesListParams {
  page?: number;       // 页码，默认1
  page_size?: number;  // 每页数量，默认20
  category?: string;   // 模板分类筛选
  is_public?: boolean;  // 是否公开模板
  search?: string;     // 搜索关键词
}

// ==================== State 状态 ====================

interface CanvasState {
  items: CanvasListItem[];       // 画布列表
  templates: CanvasTemplates[];     // 模板列表
  currentCanvas: Canvas | null;   // 当前画布
  total: number;                 // 总数量
  page: number;                   // 当前页码
  page_size: number;               // 每页数量
  loading: boolean;                // 加载状态
  error: string | null;           // 错误信息
}

const initialState: CanvasState = {
  items: [],
  templates: [],
  currentCanvas: null,
  total: 0,
  page: 1,
  page_size: 20,
  loading: false,
  error: null,
};

// ==================== Async Thunks 异步操作 ====================

/**
 * 列出画布
 * GET /api/pipeline/canvas
 */
export const fetchCanvasList = createAsyncThunk(
  'canvas/fetchCanvasList',
  async (params?: CanvasListParams) => {
    const query = new URLSearchParams();
    if (params?.org_id) query.set('org_id', params.org_id);
    if (params?.team !== undefined) query.set('team', String(params.team));
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    const response = await get<FetchCanvasListApiResponse>(
      `/api/pipeline/canvas${query.toString() ? `?${query.toString()}` : ''}`
    );

    return {
      items: response.data.data,
      total: response.data.total,
      page: response.data.page,
      page_size: response.data.size,
    };
  }
);

/**
 * 获取画布详情
 * GET /api/pipeline/canvas/:id
 */
export const fetchCanvasDetail = createAsyncThunk(
  'canvas/fetchCanvasDetail',
  async (id: string) => {
    const data = await get<{ code: string; message: string; data: Canvas }>(`/api/pipeline/canvas/${id}`);
    if (!isCanvasApiSuccess(data.code)) throw new Error(data.message || 'Failed to fetch canvas detail');
    return data.data;
  }
);

/**
 * 创建新画布
 * POST /api/pipeline/canvas
 */
export const createCanvas = createAsyncThunk(
  'canvas/createCanvas',
  async (params: CreateCanvasParams) => {
    const response = await post<{ code: number; message: string; data: CreateCanvasResult }>(
      '/api/pipeline/canvas',
      params,
    );
    return response.data;
  }
);

/**
 * 更新画布信息
 * POST /api/pipeline/canvas/:id/update
 */
export const updateCanvas = createAsyncThunk(
  'canvas/updateCanvas',
  async ({ id, params }: { id: string; params: UpdateCanvasParams }) => {
    const data = await post<Canvas | CanvasMutationEnvelope<Canvas>>(
      `/api/pipeline/canvas/${id}/update`,
      params,
    );

    // 接口可能直接返回 Canvas 对象（无 code 字段），也可能是 { code, message, data } 格式
    if (isCanvasMutationEnvelope(data)) {
      if (!isCanvasApiSuccess(data.code)) {
        throw new Error(data.message || 'Failed to update canvas');
      }

      return data.data ?? (data as unknown as Canvas);
    }

    return data;
  }
);

/**
 * 保存画布（完整更新）
 * POST /api/pipeline/canvas/:id/save
 */
export const saveCanvas = createAsyncThunk(
  'canvas/saveCanvas',
  async ({ id, params }: { id: string; params: SaveCanvasParams }) => {
    const authData = storage.getAuthData();
    const userId = authData.userId || '';

    const fullParams = {
      ...params,
      org_id: params.org_id || 'default-org',
      updated_by: params.updated_by || userId,
      nodes: params.nodes?.map((node) => ({
        ...node,
        created_by: node.created_by || userId,
        created_by_role: node.created_by_role || 'admin',
      })),
      connections: params.connections?.map((conn) => ({
        ...conn,
        created_by: conn.created_by || userId,
        created_by_role: conn.created_by_role || 'admin',
      })),
    };

    const response = await post<Canvas | CanvasMutationEnvelope<Canvas>>(
      `/api/pipeline/canvas/${id}/save`,
      fullParams,
    );

    if (isCanvasMutationEnvelope(response)) {
      if (!isCanvasApiSuccess(response.code)) {
        throw new Error(response.message || 'Failed to save canvas');
      }

      return response.data ?? (response as unknown as Canvas);
    }

    return response;
  }
);

/**
 * 删除画布
 * GET /api/pipeline/canvas/:id/delete
 */
export const deleteCanvas = createAsyncThunk(
  'canvas/deleteCanvas',
  async (id: string) => {
    await post<{ code: number; message: string; data: Record<string, never> }>(`/api/pipeline/canvas/${id}/delete`);
    return id;
  }
);

/**
 * 个人画布转入团队
 * POST /api/pipeline/canvas/:id/transfer-to-team
 */
export const transferCanvasToTeam = createAsyncThunk(
  'canvas/transferToTeam',
  async (
    { canvasId, teamId }: { canvasId: string; teamId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await post<{ code: number; message: string; data: CanvasTransferResult }>(
        `/api/pipeline/canvas/${canvasId}/transfer-to-team`,
        { team_id: teamId },
        { silent: true },
      );
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to transfer canvas to team';
      return rejectWithValue(message);
    }
  }
);

/**
 * 团队画布转回个人空间
 * POST /api/pipeline/canvas/:id/transfer-to-personal
 */
export const transferCanvasToPersonal = createAsyncThunk(
  'canvas/transferToPersonal',
  async (canvasId: string, { rejectWithValue }) => {
    try {
      const response = await post<{ code: number; message: string; data: CanvasTransferResult }>(
        `/api/pipeline/canvas/${canvasId}/transfer-to-personal`,
        undefined,
        { silent: true },
      );
      return response.data;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to transfer canvas to personal space';
      return rejectWithValue(message);
    }
  }
);

/**
 * 批量提交画布节点与连接变更
 * POST /api/pipeline/canvas/:id/batch
 */
export const batchCanvasChanges = createAsyncThunk(
  'canvas/batchChanges',
  async ({ canvasId, params }: { canvasId: string; params: BatchCanvasChangesParams }) => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= BATCH_CANVAS_CHANGES_MAX_ATTEMPTS; attempt += 1) {
      try {
        const response = await post<{ code?: number | string; message?: string; data: BatchCanvasChangesResult }>(
          `/api/pipeline/canvas/${canvasId}/batch`,
          params,
          { silent: attempt < BATCH_CANVAS_CHANGES_MAX_ATTEMPTS },
        );

        if (response.code !== undefined && !isCanvasApiSuccess(response.code)) {
          throw new Error(response.message || 'Failed to commit canvas changes');
        }

        return response.data;
      } catch (error) {
        lastError = error;

        if (attempt >= BATCH_CANVAS_CHANGES_MAX_ATTEMPTS || !shouldRetryBatchCanvasChanges(error)) {
          throw error;
        }

        await waitForBatchCanvasRetry(BATCH_CANVAS_CHANGES_RETRY_DELAY_MS * attempt);
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Failed to commit canvas changes');
  }
);

/**
 * 签发协同房间 access token
 * POST /api/pipeline/canvas/:id/collaboration/auth
 */
export const fetchCanvasCollaborationAuth = createAsyncThunk(
  'canvas/fetchCanvasCollaborationAuth',
  async ({ canvasId, share_token }: CanvasCollaborationAuthParams) => {
    const response = await post<CanvasApiEnvelope<CanvasCollaborationAuthResult>>(
      `/api/pipeline/canvas/${canvasId}/collaboration/auth`,
      share_token ? { share_token } : {},
      { silent: true },
    );

    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to authorize canvas collaboration');
    }

    return response.data;
  },
);

/**
 * 获取协同 bootstrap 快照
 * GET /api/pipeline/canvas/:id/collaboration/bootstrap
 */
export const fetchCanvasCollaborationBootstrap = createAsyncThunk(
  'canvas/fetchCanvasCollaborationBootstrap',
  async ({ canvasId, share_token }: CanvasCollaborationBootstrapParams) => {
    const query = buildShareTokenQuery(share_token);
    const response = await get<CanvasApiEnvelope<CanvasCollaborationBootstrapResult>>(
      `/api/pipeline/canvas/${canvasId}/collaboration/bootstrap${query ? `?${query}` : ''}`,
      { silent: true },
    );

    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to fetch canvas collaboration bootstrap');
    }

    return response.data;
  },
);

/**
 * 完成协同 bootstrap
 * POST /api/pipeline/canvas/:id/collaboration/bootstrap/complete
 */
export const completeCanvasCollaborationBootstrap = createAsyncThunk(
  'canvas/completeCanvasCollaborationBootstrap',
  async ({ canvasId, ...params }: CompleteCanvasCollaborationBootstrapParams) => {
    const response = await post<CanvasApiEnvelope<CompleteCanvasCollaborationBootstrapResult>>(
      `/api/pipeline/canvas/${canvasId}/collaboration/bootstrap/complete`,
      params,
      { silent: true },
    );

    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to complete canvas collaboration bootstrap');
    }

    return response.data;
  },
);

/**
 * 协同文档快照刷盘
 * POST /api/pipeline/canvas/:id/collaboration/snapshot
 */
export const saveCanvasCollaborationSnapshot = createAsyncThunk(
  'canvas/saveCanvasCollaborationSnapshot',
  async ({ canvasId, ...params }: SaveCanvasCollaborationSnapshotParams) => {
    const response = await post<CanvasApiEnvelope<SaveCanvasCollaborationSnapshotResult>>(
      `/api/pipeline/canvas/${canvasId}/collaboration/snapshot`,
      params,
      { silent: true },
    );

    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to save canvas collaboration snapshot');
    }

    return response.data;
  },
);

/**
 * 查询协同快照历史
 * GET /api/pipeline/canvas/:id/collaboration/history
 */
export const fetchCanvasCollaborationHistory = createAsyncThunk(
  'canvas/fetchCanvasCollaborationHistory',
  async ({ canvasId, page, page_size, share_token }: CanvasCollaborationHistoryParams) => {
    const query = new URLSearchParams();
    if (page) {
      query.set('page', String(page));
    }
    if (page_size) {
      query.set('page_size', String(page_size));
    }
    if (share_token) {
      query.set('share_token', share_token);
    }

    const response = await get<CanvasApiEnvelope<CanvasCollaborationHistoryResult>>(
      `/api/pipeline/canvas/${canvasId}/collaboration/history${query.toString() ? `?${query.toString()}` : ''}`,
      { silent: true },
    );

    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to fetch canvas collaboration history');
    }

    return response.data;
  },
);

/**
 * 从历史版本恢复协同快照
 * POST /api/pipeline/canvas/:id/collaboration/restore
 */
export const restoreCanvasCollaborationSnapshot = createAsyncThunk(
  'canvas/restoreCanvasCollaborationSnapshot',
  async ({ canvasId, snapshot_version }: RestoreCanvasCollaborationSnapshotParams) => {
    const response = await post<CanvasApiEnvelope<RestoreCanvasCollaborationSnapshotResult>>(
      `/api/pipeline/canvas/${canvasId}/collaboration/restore`,
      { snapshot_version },
      { silent: true },
    );

    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to restore canvas collaboration snapshot');
    }

    return response.data;
  },
);

export async function batchPasteCanvasNodes(
  canvasId: string,
  params: BatchPasteCanvasParams,
): Promise<BatchPasteCanvasResult> {
  const response = await post<CanvasMutationEnvelope<BatchPasteCanvasResult> | BatchPasteCanvasResult>(
    `/api/pipeline/canvas/${canvasId}/batch-paste-nodes`,
    params,
  );

  if (isCanvasMutationEnvelope(response)) {
    if (!isCanvasApiSuccess(response.code)) {
      throw new Error(response.message || 'Failed to paste nodes across canvases');
    }

    if (!response.data) {
      throw new Error('Failed to paste nodes across canvases');
    }

    return response.data;
  }

  return response;
}

/**
 * 列出画布模板
 * GET /api/v1/canvas-templates
 */
export const fetchTemplatesList = createAsyncThunk(
  'canvas/fetchTemplatesList',
  async (params?: TemplatesListParams) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.category) query.set('category', params.category);
    if (params?.is_public !== undefined) query.set('is_public', String(params.is_public));
    if (params?.search) query.set('search', params.search);
    const data = await get<{ code: string; message: string; data: TemplatesListResponse }>(`/api/v1/canvas-templates${query.toString() ? `?${query.toString()}` : ''}`);
    if (!isCanvasApiSuccess(data.code)) throw new Error(data.message || 'Failed to fetch templates');
    return data.data;
  }
);

/**
 * 获取画布模板详情
 * GET /api/v1/canvas-templates/:id
 */
export const fetchTemplateDetail = createAsyncThunk(
  'canvas/fetchTemplateDetail',
  async (id: string) => {
    const data = await get<{ message: string; code: string; data: CanvasTemplates }>(`/api/v1/canvas-templates/${id}`);
    if (!isCanvasApiSuccess(data.code)) throw new Error(data.message || 'Failed to fetch template detail');
    return data.data;
  }
);

/**
 * 从模板创建画布
 * POST /api/v1/canvas-templates/create-from-template
 */
export const createFromTemplate = createAsyncThunk(
  'canvas/createFromTemplate',
  async (params: CreateFromTemplateParams) => {
    const data = await post<{ code: string; message: string; data: Canvas }>('/api/v1/canvas-templates/create-from-template', params);
    if (!isCanvasApiSuccess(data.code)) throw new Error(data.message || 'Failed to create from template');
    return data.data;
  }
);

// ==================== Slice ====================

const canvasSlice = createSlice({
  name: 'canvas',
  initialState,
  reducers: {
    /** 设置当前画布 */
    setCurrentCanvas: (state, action: PayloadAction<Canvas | null>) => {
      state.currentCanvas = action.payload;
    },
    /** 设置加载状态 */
    setCanvasLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    /** 设置错误信息 */
    setCanvasError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    /** 清除错误信息 */
    clearCanvasError: (state) => {
      state.error = null;
    },
    /** 更新画布节点（本地更新） */
    updateCanvasNodes: (state, action: PayloadAction<CanvasNode[]>) => {
      if (state.currentCanvas) {
        state.currentCanvas.nodes = action.payload;
      }
    },
    /** 更新画布连接（本地更新） */
    updateCanvasConnections: (state, action: PayloadAction<CanvasConnection[]>) => {
      if (state.currentCanvas) {
        state.currentCanvas.connections = action.payload;
      }
    },
    /** 添加节点到画布（本地更新） */
    addNodeToCanvas: (state, action: PayloadAction<CanvasNode>) => {
      if (state.currentCanvas) {
        state.currentCanvas.nodes.push(action.payload);
      }
    },
    /** 从画布移除节点（本地更新） */
    removeNodeFromCanvas: (state, action: PayloadAction<string>) => {
      if (state.currentCanvas) {
        state.currentCanvas.nodes = state.currentCanvas.nodes.filter(n => n.id !== action.payload);
      }
    },
    /** 添加连接到画布（本地更新） */
    addConnectionToCanvas: (state, action: PayloadAction<CanvasConnection>) => {
      if (state.currentCanvas) {
        state.currentCanvas.connections.push(action.payload);
      }
    },
    /** 从画布移除连接（本地更新） */
    removeConnectionFromCanvas: (state, action: PayloadAction<string>) => {
      if (state.currentCanvas) {
        state.currentCanvas.connections = state.currentCanvas.connections.filter(c => c.id !== action.payload);
      }
    },
    /** 重置画布状态 */
    resetCanvasState: (state) => {
      state.currentCanvas = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 列出画布
      .addCase(fetchCanvasList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCanvasList.fulfilled, (state, action) => {
        state.loading = false;
        // page > 1 时追加（无限滚动），否则替换（首次加载/刷新）
        if (action.payload.page > 1) {
          state.items = [...state.items, ...action.payload.items];
        } else {
          state.items = action.payload.items;
        }
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.page_size = action.payload.page_size;
      })
      .addCase(fetchCanvasList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch canvas list';
      })
      // 获取画布详情
      .addCase(fetchCanvasDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCanvasDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCanvas = action.payload;
      })
      .addCase(fetchCanvasDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch canvas detail';
      })
      // 创建画布
      .addCase(createCanvas.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
      })
      // 更新画布
      .addCase(updateCanvas.fulfilled, (state, action) => {
        if (state.currentCanvas && state.currentCanvas.id === action.payload.id) {
          state.currentCanvas = { ...state.currentCanvas, ...action.payload };
        }
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = { ...state.items[index], ...action.payload };
        }
      })
      // 保存画布
      .addCase(saveCanvas.fulfilled, (state, action) => {
        if (state.currentCanvas && state.currentCanvas.id === action.payload.id) {
          state.currentCanvas = { ...state.currentCanvas, ...action.payload };
        }
      })
      // 删除画布
      .addCase(deleteCanvas.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.total -= 1;
        if (state.currentCanvas?.id === action.payload) {
          state.currentCanvas = null;
        }
      })
      .addCase(transferCanvasToTeam.fulfilled, (state, action) => {
        if (state.currentCanvas?.id === action.payload.id) {
          state.currentCanvas = {
            ...state.currentCanvas,
            name: action.payload.title ?? action.payload.name ?? state.currentCanvas.name,
            visibility: action.payload.visibility ?? state.currentCanvas.visibility,
            team_id: action.payload.team_id ?? null,
            owner_id: action.payload.owner_id ?? null,
            transfer_source: action.payload.transfer_source ?? null,
            created_by: action.payload.created_by ?? state.currentCanvas.created_by,
            updated_at: action.payload.updated_at ?? state.currentCanvas.updated_at,
          };
        }

        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = {
            ...state.items[index],
            name: action.payload.title ?? action.payload.name ?? state.items[index].name,
            visibility: action.payload.visibility ?? state.items[index].visibility,
            team_id: action.payload.team_id ?? null,
            owner_id: action.payload.owner_id ?? null,
            transfer_source: action.payload.transfer_source ?? null,
            created_by: action.payload.created_by ?? state.items[index].created_by,
            updated_at: action.payload.updated_at ?? state.items[index].updated_at,
          };
        }
      })
      .addCase(transferCanvasToPersonal.fulfilled, (state, action) => {
        if (state.currentCanvas?.id === action.payload.id) {
          state.currentCanvas = {
            ...state.currentCanvas,
            name: action.payload.title ?? action.payload.name ?? state.currentCanvas.name,
            visibility: action.payload.visibility ?? state.currentCanvas.visibility,
            team_id: action.payload.team_id ?? null,
            owner_id: action.payload.owner_id ?? null,
            transfer_source: action.payload.transfer_source ?? null,
            created_by: action.payload.created_by ?? state.currentCanvas.created_by,
            updated_at: action.payload.updated_at ?? state.currentCanvas.updated_at,
          };
        }

        const index = state.items.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = {
            ...state.items[index],
            name: action.payload.title ?? action.payload.name ?? state.items[index].name,
            visibility: action.payload.visibility ?? state.items[index].visibility,
            team_id: action.payload.team_id ?? null,
            owner_id: action.payload.owner_id ?? null,
            transfer_source: action.payload.transfer_source ?? null,
            created_by: action.payload.created_by ?? state.items[index].created_by,
            updated_at: action.payload.updated_at ?? state.items[index].updated_at,
          };
        }
      })
      // 批量提交节点与连接
      .addCase(batchCanvasChanges.fulfilled, (state, action) => {
        const currentCanvas = state.currentCanvas;

        if (!currentCanvas) {
          return;
        }

        const deletedNodeIds = new Set(action.payload.nodes.deleted);
        const deletedConnectionIds = new Set(action.payload.connections.deleted);

        currentCanvas.nodes = currentCanvas.nodes
          .filter((node) => !deletedNodeIds.has(node.id));

        action.payload.nodes.created.forEach((node) => {
          currentCanvas.nodes = upsertCanvasNode(currentCanvas.nodes, node);
        });

        action.payload.nodes.updated.forEach((node) => {
          currentCanvas.nodes = upsertCanvasNode(currentCanvas.nodes, node);
        });

        currentCanvas.connections = currentCanvas.connections
          .filter((connection) => (
            !deletedConnectionIds.has(connection.id)
            && !deletedNodeIds.has(connection.source)
            && !deletedNodeIds.has(connection.target)
          ));

        action.payload.connections.created.forEach((connection) => {
          currentCanvas.connections = upsertCanvasConnection(
            currentCanvas.connections,
            connection,
          );
        });

        action.payload.connections.updated.forEach((connection) => {
          currentCanvas.connections = upsertCanvasConnection(
            currentCanvas.connections,
            connection,
          );
        });
      })
      // 列出模板
      .addCase(fetchTemplatesList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplatesList.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = action.payload.templates;
      })
      .addCase(fetchTemplatesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch templates';
      })
      // 获取模板详情
      .addCase(fetchTemplateDetail.fulfilled, (state, action) => {
        const index = state.templates.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.templates[index] = action.payload;
        }
      })
      // 从模板创建
      .addCase(createFromTemplate.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.total += 1;
        state.currentCanvas = action.payload;
      });
  },
});

export const {
  setCurrentCanvas,
  setCanvasLoading,
  setCanvasError,
  clearCanvasError,
  updateCanvasNodes,
  updateCanvasConnections,
  addNodeToCanvas,
  removeNodeFromCanvas,
  addConnectionToCanvas,
  removeConnectionFromCanvas,
  resetCanvasState,
} = canvasSlice.actions;

export default canvasSlice.reducer;
